import { useEffect, useState } from "react";
import { Button, Group, Modal, Select, Stack, Switch, Textarea, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";

const toDateString = (v) => (v ? new Date(v).toISOString().slice(0, 10) : null);
const pad = (n) => String(n).padStart(2, "0");
// A local-calendar-day key (YYYY-MM-DD); parsing it back with new Date() yields UTC midnight, which
// is exactly how the backend anchors due dates.
const localKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Create/edit form for a task. Emits the editable fields; the container assembles the full DTO
// (preset record link on create, preserved completion/candidate link on edit). When a project is
// linked, the client is derived from it (a project already belongs to a client).
export default function TaskEditModal({
  opened, onClose, onSave, saving, initial,
  userOptions, projectOptions, projectClientMap = {}, clientOptions, showLinks,
}) {
  const { t } = useTranslation("Veft");
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState(null);
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [assigneeUserId, setAssigneeUserId] = useState(null);
  const [isImportant, setIsImportant] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [clientId, setClientId] = useState(null);

  useEffect(() => {
    if (!opened) return;
    setTitle(initial?.title ?? "");
    setTitleError(null);
    setDescription(initial?.description ?? "");
    setDueDate(toDateString(initial?.dueDate));
    setAssigneeUserId(initial?.assigneeUserId ?? null);
    setIsImportant(!!initial?.isImportant);
    setProjectId(initial?.projectId ?? null);
    setClientId(initial?.clientId ?? null);
  }, [opened, initial]);

  // A project belongs to a client, so picking a project sets (and locks) the client to its own.
  const onProjectChange = (v) => {
    setProjectId(v);
    if (v) setClientId(projectClientMap[v] ?? null);
  };

  const quickDue = (days, months = 0) => {
    const d = new Date();
    if (months) d.setMonth(d.getMonth() + months);
    else d.setDate(d.getDate() + days);
    setDueDate(localKey(d));
  };

  const submit = () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setTitleError(t("tasks_title_required"));
      return;
    }
    onSave({
      title: trimmed,
      description: description.trim() || null,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      assigneeUserId: assigneeUserId || null,
      isImportant,
      ...(showLinks ? { projectId: projectId || null, clientId: clientId || null } : {}),
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} title={initial ? t("tasks_edit") : t("tasks_add")}>
      <Stack>
        <TextInput
          label={t("tasks_title_label")}
          placeholder={t("tasks_title_placeholder")}
          data-autofocus
          required
          maxLength={300}
          value={title}
          onChange={(e) => { setTitle(e.currentTarget.value); setTitleError(null); }}
          error={titleError}
        />
        <Textarea
          label={t("tasks_notes_label")}
          autosize
          minRows={2}
          maxRows={6}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Group grow align="flex-start">
          <Stack gap={6}>
            <DatePickerInput
              label={t("tasks_due_date")}
              clearable
              value={dueDate}
              onChange={setDueDate}
              popoverProps={{ withinPortal: true }}
            />
            <Group gap={4}>
              <Button variant="default" size="compact-xs" onClick={() => quickDue(0)}>{t("tasks_quick_today")}</Button>
              <Button variant="default" size="compact-xs" onClick={() => quickDue(1)}>{t("tasks_quick_tomorrow")}</Button>
              <Button variant="default" size="compact-xs" onClick={() => quickDue(7)}>{t("tasks_quick_week")}</Button>
              <Button variant="default" size="compact-xs" onClick={() => quickDue(0, 1)}>{t("tasks_quick_month")}</Button>
            </Group>
          </Stack>
          <Select
            label={t("tasks_assignee")}
            placeholder={t("tasks_unassigned")}
            data={userOptions}
            value={assigneeUserId}
            onChange={setAssigneeUserId}
            searchable
            clearable
          />
        </Group>
        {showLinks && (
          <Group grow align="flex-start">
            <Select
              label={t("tasks_link_project")}
              placeholder={t("tasks_link_none")}
              data={projectOptions}
              value={projectId}
              onChange={onProjectChange}
              searchable
              clearable
            />
            <Select
              label={t("tasks_link_client")}
              placeholder={t("tasks_link_none")}
              description={projectId ? t("tasks_client_from_project") : undefined}
              data={clientOptions}
              value={clientId}
              onChange={setClientId}
              disabled={!!projectId}
              searchable
              clearable
            />
          </Group>
        )}
        <Switch
          label={t("tasks_important")}
          checked={isImportant}
          onChange={(e) => setIsImportant(e.currentTarget.checked)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t("cancel", "Cancel")}</Button>
          <Button onClick={submit} loading={saving}>{t("save", "Save")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
